import {blocked} from './world';
export type Point={x:number;z:number};
export const NAV={step:1,margin:16,maxDistance:150,maxNodes:24000,batch:160,budgetMs:2};
export function clearLine(a:Point,b:Point){
 const n=Math.max(1,Math.ceil(Math.hypot(b.x-a.x,b.z-a.z)/.18));
 for(let i=0;i<=n;i++)if(blocked(a.x+(b.x-a.x)*i/n,a.z+(b.z-a.z)*i/n))return false;
 return true;
}
type Node=Point&{g:number;f:number;parent?:Node};
class Heap{
 a:Node[]=[];
 push(n:Node){let i=this.a.length;this.a.push(n);while(i>0){const p=(i-1)>>1;if(this.a[p].f<=n.f)break;this.a[i]=this.a[p];i=p;}this.a[i]=n;}
 pop(){const top=this.a[0],last=this.a.pop()!;if(this.a.length){let i=0;while(i*2+1<this.a.length){let c=i*2+1;if(c+1<this.a.length&&this.a[c+1].f<this.a[c].f)c++;if(this.a[c].f>=last.f)break;this.a[i]=this.a[c];i=c;}this.a[i]=last;}return top;}
}
export class Planner{
 done=false;path:Point[]=[];reason='';expanded=0;
 private heap=new Heap();private costs=new Map<string,number>();private closed=new Set<string>();private walkable=new Map<string,boolean>();
 private minX:number;private maxX:number;private minZ:number;private maxZ:number;
 constructor(private start:Point,private goal:Point){
  this.minX=Math.max(-499,Math.floor(Math.min(start.x,goal.x))-NAV.margin);this.maxX=Math.min(499,Math.ceil(Math.max(start.x,goal.x))+NAV.margin);
  this.minZ=Math.max(-499,Math.floor(Math.min(start.z,goal.z))-NAV.margin);this.maxZ=Math.min(499,Math.ceil(Math.max(start.z,goal.z))+NAV.margin);
  if(![start.x,start.z,goal.x,goal.z].every(Number.isFinite)||blocked(goal.x,goal.z)){this.fail('Непроходимая точка');return;}
  if(Math.hypot(goal.x-start.x,goal.z-start.z)>NAV.maxDistance){this.fail('Цель дальше 150 м');return;}
  if(clearLine(start,goal)){this.done=true;this.path=[goal];return;}
  let root:Point|undefined;
  for(let r=0;r<=2&&!root;r++)for(let dx=-r;dx<=r&&!root;dx++)for(let dz=-r;dz<=r;dz++){
   const p={x:Math.round(start.x)+dx,z:Math.round(start.z)+dz};if(!blocked(p.x,p.z)&&clearLine(start,p)){root=p;break;}
  }
  if(!root){this.fail('Нет безопасного выхода из стартовой точки');return;}
  const n={...root,g:0,f:Math.hypot(root.x-goal.x,root.z-goal.z)};this.heap.push(n);this.costs.set(this.key(n),0);
 }
 private key(p:Point){return p.x+','+p.z;}
 private free(x:number,z:number){const key=x+','+z;if(!this.walkable.has(key))this.walkable.set(key,!blocked(x,z));return this.walkable.get(key)!;}
 private fail(reason:string){this.done=true;this.reason=reason;this.path=[];this.heap.a=[];}
 step(){
  if(this.done)return;const begin=performance.now();let count=0;
  while(this.heap.a.length&&count++<NAV.batch&&performance.now()-begin<NAV.budgetMs){
   const n=this.heap.pop(),key=this.key(n);if(this.closed.has(key))continue;this.closed.add(key);
   if(++this.expanded>NAV.maxNodes){this.fail('Лимит локального поиска');return;}
   if(Math.hypot(n.x-this.goal.x,n.z-this.goal.z)<1.5&&clearLine(n,this.goal)){
    const path:Point[]=[this.goal];let p:Node|undefined=n;while(p){path.push({x:p.x,z:p.z});p=p.parent;}path.push(this.start);path.reverse();
    // O(n) collinear compression only: every retained segment is a union of validated edges.
    const smooth:Point[]=[];
    for(const point of path){
     while(smooth.length>=2){
      const a=smooth[smooth.length-2],b=smooth[smooth.length-1],ux=b.x-a.x,uz=b.z-a.z,vx=point.x-b.x,vz=point.z-b.z;
      if(Math.abs(ux*vz-uz*vx)>1e-7||ux*vx+uz*vz<=0)break;smooth.pop();
     }
     smooth.push(point);
    }
    if(smooth.length&&Math.hypot(smooth[0].x-this.start.x,smooth[0].z-this.start.z)<.01)smooth.shift();
    this.path=smooth;this.done=true;this.heap.a=[];return;
   }
   for(let dx=-1;dx<=1;dx++)for(let dz=-1;dz<=1;dz++){
    if(!dx&&!dz)continue;const x=n.x+dx,z=n.z+dz,k=x+','+z;
    if(x<this.minX||x>this.maxX||z<this.minZ||z>this.maxZ||this.closed.has(k)||!this.free(x,z))continue;
    if(dx&&dz&&(!this.free(n.x+dx,n.z)||!this.free(n.x,n.z+dz)))continue;
    const g=n.g+Math.hypot(dx,dz);if(g>=(this.costs.get(k)??Infinity)||!clearLine(n,{x,z}))continue;
    this.costs.set(k,g);this.heap.push({x,z,g,f:g+Math.hypot(x-this.goal.x,z-this.goal.z),parent:n});
   }
  }
  if(!this.heap.a.length)this.fail('Путь не найден в локальном окне');
 }
}
